import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAppDispatch } from "../app/hooks";
import { logout, syncFromStorage } from "../features/auth/authSlice";
import { authService } from "../features/auth/authService";
import { returnPath } from "../features/auth/returnPath";
import { tokenHandler } from "../utils/tokenHandler";

// There is no idle logout: the user asked for it to go (2026-09-21). A login
// ends only on the Logout button, on the server's session limit
// (jwt.session-max-age), or on a 401.
const CHECK_INTERVAL = 60_000;
// A token lives 7 days (jwt.expiration). Renewing only in its last 5 minutes
// needed a tab open in exactly that window, a week after login, and nobody's
// was. Swapped once a day instead, every working day starts a fresh 7 days
// and only the 30-day session limit ends the login.
const RENEW_AFTER = 24 * 60 * 60 * 1000;
let sessionRedirectInFlight = false;

export const useTokenRefresh = () => {
  const dispatch = useAppDispatch();
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const performLogout = () => {
    if (sessionRedirectInFlight) {
      return;
    }

    sessionRedirectInFlight = true;

    // Before the session is cleared: the next login returns to this screen.
    returnPath.save();

    tokenHandler.handleExpired(() => {
      void dispatch(logout());
      if (window.location.pathname !== "/" || window.location.search || window.location.hash) {
        window.history.replaceState(null, "", "/");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    });

    window.setTimeout(() => {
      sessionRedirectInFlight = false;
    }, 1000);
  };

  const { mutateAsync: refreshToken } = useMutation({
    mutationFn: authService.refreshToken,
    onSuccess: (data) => {
      if (typeof data?.token === "string" && data.token.trim().length > 0) {
        dispatch(syncFromStorage());
      }
    },
    onError: (error: any) => {
      console.error("Token refresh failed:", error);
      // Only log out if it is an authentication/authorization failure (401/403)
      if (error?.status === 401 || error?.status === 403) {
        performLogout();
      }
    },
  });

  const checkToken = async () => {
    const token = tokenHandler.get();
    if (!token) {
      return;
    }

    if (tokenHandler.hasExceededSessionMaxAge(token)) {
      performLogout();
      return;
    }

    const issuedAt = tokenHandler.decodePayload(token)?.iat;
    const dueForRenewal =
      typeof issuedAt === "number" && Date.now() - issuedAt * 1000 >= RENEW_AFTER;

    if (dueForRenewal || !tokenHandler.isValid(5)) {
      try {
        await refreshToken();
      } catch {
        // React Query onError handles the failure path.
      }
    }
  };

  useEffect(() => {
    checkIntervalRef.current = setInterval(checkToken, CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  return { refreshToken };
};
